import { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { DateTime } from "luxon";
import { observer } from "mobx-react-lite";

import {
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSButton,
  MDSButtonVariant,
  MDSSelect,
  MDSMenuContentItem,
  MDSMenuContentItemKind,
} from "@/mds";
import { useAppStore } from "@/store";
import { useSearchParams } from "react-router-dom";
import {
  AudioRecordingProcessingStatus,
  AudioRecordingRecordingStatus,
  AudioRecordingUploadStatus,
} from "@/store/audio-recordings";
import type { AudioRecordingObservable } from "@/store/audio-recordings";
import {
  AudioRecordingSyncStatusCard,
  CapturedAudioArtifactCard,
  TranscriptionSectionContent,
} from "@/pages/audio-recordings/components";
import {
  AudioRecordingCard,
  AudioRecordingCardBody,
  AudioRecordingCardHeader,
  useLocalAudioArtifactDebugData,
} from "@/pages/audio-recordings/shared";
import type { Uuid } from "@/domains/global/identifiers";

const formatLocalDateTime = (value?: string | null) => {
  if (!value) return "—";
  const dt = DateTime.fromISO(value);
  if (!dt.isValid) return value;
  return dt.toLocaleString(DateTime.DATETIME_MED);
};

export const AudioRecordingActiveStatePage = observer(() => {
  const { store } = useAppStore();
  const audioStore = store.audio;
  const recordingStore = audioStore.recording;

  const [searchParams, setSearchParams] = useSearchParams();

  const initialRecordingId = useMemo(() => {
    const paramValue = searchParams.get("audioRecordingId");
    if (paramValue) {
      return paramValue as Uuid;
    }
    return audioStore.activeRecordingId;
  }, [audioStore.activeRecordingId, searchParams]);

  const [selectedRecordingId, setSelectedRecordingId] = useState<Uuid | null>(
    initialRecordingId ?? null
  );
  const [statusError, setStatusError] = useState<string | null>(null);

  const knownIds = Array.from(store.audioRecordings.ids.values());
  if (selectedRecordingId && !knownIds.includes(selectedRecordingId)) {
    knownIds.unshift(selectedRecordingId);
  }
  const selectItems: MDSMenuContentItem[] = knownIds.map(id => ({
    id,
    label: id,
    kind: MDSMenuContentItemKind.Item,
  }));

  useEffect(() => {
    if (!selectedRecordingId && audioStore.activeRecordingId) {
      setSelectedRecordingId(audioStore.activeRecordingId);
    }
  }, [audioStore.activeRecordingId, selectedRecordingId]);

  useEffect(() => {
    if (!selectedRecordingId && selectItems.length) {
      setSelectedRecordingId(selectItems[0].id as Uuid);
    }
  }, [selectedRecordingId, selectItems]);

  useEffect(() => {
    const currentParam = searchParams.get("audioRecordingId");
    const nextValue = selectedRecordingId ?? undefined;
    if (nextValue === currentParam) return;

    const nextParams = new URLSearchParams(searchParams);
    if (nextValue) {
      nextParams.set("audioRecordingId", nextValue);
    } else {
      nextParams.delete("audioRecordingId");
    }
    setSearchParams(nextParams);
  }, [searchParams, selectedRecordingId, setSearchParams]);

  useEffect(() => {
    if (!selectedRecordingId) {
      setStatusError(null);
      return;
    }

    setStatusError(null);
    void store.audioRecordings.getAsync(selectedRecordingId);
  }, [selectedRecordingId, store.audioRecordings]);

  const recording: AudioRecordingObservable | undefined = (() => {
    if (!selectedRecordingId) return undefined;
    return store.audioRecordings.get(selectedRecordingId);
  })();
  const {
    data: localArtifactData,
    error: localArtifactError,
    isLoading: isLocalArtifactLoading,
    refresh: refreshLocalArtifactData,
  } = useLocalAudioArtifactDebugData({
    audioRecordingId: selectedRecordingId,
    recordingStrategy: recording?.recordingStrategy ?? null,
  });

  const hasActiveSession = Boolean(audioStore.activeRecordingId);
  const recordingStatus = (recording?.recordingStatus ??
    AudioRecordingRecordingStatus.INITIAL) as AudioRecordingRecordingStatus;
  const uploadStatus = (recording?.uploadStatus ??
    AudioRecordingUploadStatus.INITIAL) as AudioRecordingUploadStatus;
  const processingStatus = (recording?.processingStatus ??
    AudioRecordingProcessingStatus.INITIAL) as AudioRecordingProcessingStatus;
  const isPaused = recordingStore.isPaused;
  const isRecording = recordingStatus === AudioRecordingRecordingStatus.IN_PROGRESS && !isPaused;
  const canStart =
    !isRecording &&
    selectedRecordingId &&
    recordingStatus === AudioRecordingRecordingStatus.INITIAL;
  const canResume =
    isPaused &&
    selectedRecordingId &&
    recordingStatus === AudioRecordingRecordingStatus.IN_PROGRESS;
  const canPause = isRecording && selectedRecordingId;
  const canFinish =
    selectedRecordingId && recordingStatus === AudioRecordingRecordingStatus.IN_PROGRESS;
  const canClear = Boolean(selectedRecordingId);
  const handleActivate = async () => {
    if (!selectedRecordingId) return;
    setStatusError(null);
    try {
      await audioStore._activateRecording({ audioRecordingId: selectedRecordingId });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to activate recording.";
      setStatusError(message);
    }
  };

  const handleDeactivate = async () => {
    setStatusError(null);
    try {
      await audioStore._deactivateRecording();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to deactivate.";
      setStatusError(message);
    }
  };

  const handleStart = () => {
    if (!selectedRecordingId || !canStart) return;
    setStatusError(null);
    void audioStore.createAndStartAudioRecording({ audioRecordingId: selectedRecordingId });
  };

  const handlePause = () => {
    if (!selectedRecordingId || !canPause) return;
    setStatusError(null);
    void audioStore.pauseRecording({ audioRecordingId: selectedRecordingId });
  };

  const handleResume = () => {
    if (!selectedRecordingId || !canResume) return;
    setStatusError(null);
    void audioStore.resumeRecording({ audioRecordingId: selectedRecordingId });
  };

  const handleFinish = () => {
    if (!selectedRecordingId || !canFinish) return;
    setStatusError(null);
    void audioStore.finishRecording({ audioRecordingId: selectedRecordingId });
  };

  const handleTrash = () => {
    if (!selectedRecordingId || !canClear) return;
    if (!window.confirm("Trash local audio and transcript data for this recording?")) return;
    setStatusError(null);
    void audioStore.trashRecording({ audioRecordingId: selectedRecordingId });
    void refreshLocalArtifactData({ showSpinner: false });
  };

  const handleRefreshLocalArtifactData = () => {
    void refreshLocalArtifactData();
  };

  const handleViewRecording = () => {
    if (!selectedRecordingId) return;
    store.navigation.goToAudioRecording({ recordingId: selectedRecordingId, openInSameTab: true });
  };

  const viewButtonText = selectedRecordingId
    ? `View recording ${selectedRecordingId.slice(0, 6)}...`
    : "View recording";

  const telemetryFields = (() => {
    const uploadProgress = recording?.isUploading
      ? `${recording.uploadProgressPercent ?? 0}% (uploading)`
      : recording?.uploadProgressPercent != null
        ? `${recording.uploadProgressPercent}%`
        : "—";

    const microphoneLabel = recordingStore.isMicrophoneDisconnected
      ? "Disconnected"
      : (recordingStore.activeAudioInputDeviceLabel ?? "Connected");

    const waveformSummary = recordingStore.waveformSamples
      ? `${recordingStore.waveformSamples.sampleRate} Hz, ${recordingStore.waveformSamples.pcm.length} samples`
      : "—";

    const controllersLabel = recordingStore.debugActiveRecordings.length
      ? recordingStore.debugActiveRecordings
          .map(
            state => `${state.audioRecordingId} (transcripts=${state.initialTranscriptTurnOrder})`
          )
          .join("; ")
      : "—";

    const formattedDuration = recording?.formattedDuration ?? "00:00";
    const uploadErrorMessage = recording?.uploadErrorMessage ?? null;
    const transformErrorMessage = recording?.transformErrorMessage ?? null;
    const errorMessage = transformErrorMessage ?? uploadErrorMessage ?? "—";

    return [
      { label: "Elapsed time", value: formattedDuration },
      { label: "Upload progress", value: uploadProgress },
      { label: "Microphone", value: microphoneLabel },
      { label: "Waveform samples", value: waveformSummary },
      { label: "Active controllers", value: controllersLabel },
      { label: "Paused", value: isPaused ? "Yes" : "No" },
      { label: "Last error", value: errorMessage },
    ];
  })();

  return (
    <ContentStack>
      <AudioRecordingCard>
        <AudioRecordingCardHeader title="Active recording state" />
        <AudioRecordingCardBody>
          <SelectorRow>
            <FieldStack>
              <Label variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                Select recording
              </Label>
              <MDSSelect
                label="Active recording"
                aria-label="Active recording"
                items={selectItems}
                selectedKey={selectedRecordingId ?? null}
                onSelectionChange={key => {
                  if (key === null) {
                    setSelectedRecordingId(null);
                    return;
                  }
                  setSelectedRecordingId(key as Uuid);
                }}
                buttonVariant={MDSButtonVariant.Transparent}
                isDisabled={!selectItems.length}
              />
            </FieldStack>
            <MDSButton
              onClick={handleViewRecording}
              variant={MDSButtonVariant.Transparent}
              isDisabled={!selectedRecordingId}
            >
              {viewButtonText}
            </MDSButton>
            <MDSButton
              onClick={handleActivate}
              variant={MDSButtonVariant.Emphasis}
              isDisabled={!selectedRecordingId}
            >
              Activate
            </MDSButton>
          </SelectorRow>
          <StatusRow>
            <StatusItem>
              <StatusLabel variant={MDSTextVariant.TextXs}>Active recording</StatusLabel>
              <StatusValue variant={MDSTextVariant.TextSm}>
                {audioStore.activeRecordingId ?? "None"}
              </StatusValue>
            </StatusItem>
            <StatusItem>
              <StatusLabel variant={MDSTextVariant.TextXs}>Note</StatusLabel>
              <StatusValue variant={MDSTextVariant.TextSm}>
                {recording?.associatedNoteId ?? "—"}
              </StatusValue>
            </StatusItem>
            <StatusItem>
              <StatusLabel variant={MDSTextVariant.TextXs}>Created</StatusLabel>
              <StatusValue variant={MDSTextVariant.TextSm}>
                {formatLocalDateTime(recording?.locallyCreatedAt)}
              </StatusValue>
            </StatusItem>
            <StatusItem>
              <StatusLabel variant={MDSTextVariant.TextXs}>Status</StatusLabel>
              <StatusValue variant={MDSTextVariant.TextSm}>{recordingStatus}</StatusValue>
            </StatusItem>
          </StatusRow>
          <ActionsRow>
            <MDSButton
              variant={MDSButtonVariant.Transparent}
              onClick={handleDeactivate}
              isDisabled={!audioStore.activeRecordingId}
            >
              Deactivate
            </MDSButton>
          </ActionsRow>
          {statusError ? (
            <StatusMessage variant={MDSTextVariant.TextSm} $tone="error">
              {statusError}
            </StatusMessage>
          ) : null}
        </AudioRecordingCardBody>
      </AudioRecordingCard>

      <AudioRecordingCard>
        <AudioRecordingCardHeader title="UI telemetry" />
        <AudioRecordingCardBody>
          <TelemetryList>
            {telemetryFields.map(field => (
              <TelemetryItem key={field.label}>
                <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                  {field.label}
                </MDSText>
                <MDSText variant={MDSTextVariant.TextSm}>{field.value}</MDSText>
              </TelemetryItem>
            ))}
          </TelemetryList>
        </AudioRecordingCardBody>
      </AudioRecordingCard>

      {hasActiveSession && (
        <>
          <AudioRecordingCard>
            <AudioRecordingCardHeader title="Controls" />
            <AudioRecordingCardBody>
              <ButtonRow>
                <MDSButton onClick={handleStart} isDisabled={!canStart}>
                  Start
                </MDSButton>
                <MDSButton onClick={handlePause} isDisabled={!canPause}>
                  Pause
                </MDSButton>
                <MDSButton onClick={handleResume} isDisabled={!canResume}>
                  Resume
                </MDSButton>
                <MDSButton onClick={handleFinish} isDisabled={!canFinish}>
                  Finish
                </MDSButton>
                <MDSButton onClick={handleTrash} isDisabled={!canClear}>
                  Clear
                </MDSButton>
              </ButtonRow>
            </AudioRecordingCardBody>
          </AudioRecordingCard>

          <AudioRecordingSyncStatusCard
            recordingStatus={recordingStatus}
            uploadStatus={uploadStatus}
            processingStatus={processingStatus}
          />

          <AudioRecordingCard>
            <AudioRecordingCardHeader title="Transcript turns" />
            <AudioRecordingCardBody>
              <TranscriptionSectionContent recording={recording} />
            </AudioRecordingCardBody>
          </AudioRecordingCard>

          <ArtifactSection>
            <CapturedAudioArtifactCard
              data={localArtifactData}
              error={localArtifactError}
              isLoading={isLocalArtifactLoading}
              headerActions={
                <MDSButton
                  onClick={handleRefreshLocalArtifactData}
                  isDisabled={!selectedRecordingId || isLocalArtifactLoading}
                  variant={MDSButtonVariant.Transparent}
                >
                  {isLocalArtifactLoading ? "Refreshing…" : "Refresh"}
                </MDSButton>
              }
            />
          </ArtifactSection>
        </>
      )}
    </ContentStack>
  );
});

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
`;

const SelectorRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
`;

const FieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 220px;
`;

const Label = styled(MDSText)`
  font-weight: 500;
`;

const StatusRow = styled.div`
  display: grid;
  margin-top: 16px;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
`;

const StatusItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatusLabel = styled(MDSText)`
  color: ${MDSTextColor.Secondary};
`;

const StatusValue = styled(MDSText)`
  font-weight: 500;
`;

const StatusMessage = styled(MDSText)<{ $tone?: "default" | "error" }>`
  margin-top: 12px;
  color: ${({ theme, $tone }) =>
    $tone === "error" ? theme.colors.text.danger : theme.colors.text.secondary};
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  margin-top: 16px;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const TelemetryList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
`;

const TelemetryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ArtifactSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
